/**
 * `device` super-property — the client form factor, NOT Mixpanel's native $device/$os/library
 * (product separation lives on product_type, per the tracking plan). Allowed values match the
 * generated vocabulary: mobile / tablet / desktop.
 */
export type DeviceFormFactor = "mobile" | "tablet" | "desktop";

/** Width thresholds from the app's own Tailwind breakpoints (tailwind.config.js `screens`). */
const TABLET_MIN_PX = 768; // `tablet` breakpoint
const DESKTOP_MIN_PX = 1024; // intended `desktop` breakpoint

/**
 * Resolve the current form factor from the viewport width. apps/web is responsive (mobile-first),
 * so `device` is a real, computed value — never a constant. Falls back to "desktop" with no
 * `window` (SSR/headless), the desktop-first default. Pair with the matchMedia listeners in
 * useAnalyticsSuperProperties to keep it live across a resize / rotation that crosses a breakpoint.
 */
export function resolveDeviceFormFactor(): DeviceFormFactor {
  if (typeof window === "undefined") return "desktop";
  const width = window.innerWidth;
  if (width < TABLET_MIN_PX) return "mobile";
  if (width < DESKTOP_MIN_PX) return "tablet";
  return "desktop";
}

/** matchMedia queries whose `change` events fire exactly on the form-factor breakpoint crossings. */
export const DEVICE_BREAKPOINT_QUERIES = [
  `(min-width: ${TABLET_MIN_PX}px)`,
  `(min-width: ${DESKTOP_MIN_PX}px)`,
] as const;
